import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { PcrProtocol, PcrProtocolForm, PcrProtocolSearchForm } from 'entities/pcr_protocol'

type PcrProtocolsResponse = PagingResponse & {
    results: PcrProtocol[]
}

export function useFetchPcrProtocolsApi(
    searchForm?: Form<PcrProtocolSearchForm>
): IndexApiSet<PcrProtocolsResponse> & { execute: () => void; executeAndPageReset: () => void } {
    const apiPath = 'pcr_protocols/'
    const api = useIndexApi<PcrProtocolsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    const executeAndPageReset = (): void => {
        if (api.pageSet.page == 1) {
            execute()
        } else {
            api.pageSet.setPage(1)
        }
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        executeAndPageReset()
    }, [searchForm?.object])

    return { ...api, execute: execute, executeAndPageReset: executeAndPageReset }
}

export function useDownloadCsvPcrProtocolsApi(searchForm: Form<PcrProtocolSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'pcr_protocols/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'pcrProtocol', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostPcrProtocolApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrProtocolForm>) => void } {
    const apiSet = usePostApi<BaseResponse, PcrProtocolForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<PcrProtocolForm>) => {
        const apiPath = `pcr_protocols/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutPcrProtocolApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrProtocolForm>) => void } {
    const apiSet = usePutApi<BaseResponse, PcrProtocolForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<PcrProtocolForm>) => {
        const apiPath = `pcr_protocols/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutPcrProtocolsApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrProtocolForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, PcrProtocolForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<PcrProtocolForm[]>) => {
        const apiPath = `pcr_protocols/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeletePcrProtocolApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `pcr_protocols/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
